import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Badge,
  Card,
  Form,
  Image,
  Alert,
} from "@themesberg/react-bootstrap";
import moment from "moment";
import { getSaleStatus } from "../helpers/sale-status";
import { updateSalesTicket } from "../../../../controller/api/__classic__/ticketStorage";
import ImageUpload from "../../ticket-storage/sub-screen/add-ticket-popup/component/ImageUpload";

const formatNumbers = (value) => {
  const text = String(value ?? "");
  if (!text) return "-";
  if (!/^\d+$/.test(text)) return text;
  return text.split("").join(" ");
};

const normalizeTicketNumbers = (value) => String(value ?? "").replace(/\D/g, "");

const TicketDetailPopup = ({ show, onHide, ticket, cookies, onRefresh }) => {
  const [editorMode, setEditorMode] = useState("none"); // none | edit | image
  const [localTicket, setLocalTicket] = useState(() => ticket || null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formNumbers, setFormNumbers] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formImages, setFormImages] = useState("");

  useEffect(() => {
    setLocalTicket(ticket || null);
  }, [ticket]);

  const canEdit = !localTicket?.isPairGroup;
  const token = cookies?.lottmekong;
  const canUseEditor = canEdit && Boolean(token);
  const mode = editorMode === "none" ? "view" : "edit";

  const {
    id,
    channelName,
    numbers,
    amount,
    inputAmount,
    soldAmount,
    returnedAmount,
    status,
    isExpired,
    isPairGroup,
    ticketCount,
    displayNumbers,
    createdAt,
    createdByName,
    approvedByName,
    approvedAt,
    images,
  } = localTicket || {};

  const saleStatus = useMemo(
    () => getSaleStatus({ status, amount, isExpired }),
    [status, amount, isExpired],
  );

  const displayImages = images
    ? typeof images === "string" && images.startsWith("http")
      ? images
      : `data:image/jpeg;base64,${images}`
    : null;

  if (!localTicket) return null;

  const openEditTicket = () => {
    if (!canEdit) return;
    setError("");
    setFormNumbers(normalizeTicketNumbers(numbers));
    setFormAmount(String(amount ?? ""));
    setFormImages(images || "");
    setEditorMode("edit");
  };

  const openEditImage = () => {
    if (!canEdit) return;
    setError("");
    setFormNumbers(normalizeTicketNumbers(numbers));
    setFormAmount(String(amount ?? ""));
    setFormImages(images || "");
    setEditorMode("image");
  };

  const closeEditor = () => {
    setError("");
    setIsSubmitting(false);
    setEditorMode("none");
  };

  const handleHide = () => {
    closeEditor();
    onHide && onHide();
  };

  const onSubmitEdit = async ({ imageOnly } = {}) => {
    if (!token) {
      setError("Thiếu token đăng nhập");
      return;
    }

    const nextNumbers = imageOnly
      ? normalizeTicketNumbers(numbers)
      : normalizeTicketNumbers(formNumbers);
    const nextAmount = imageOnly ? Number(amount || 0) : Number(formAmount || 0);
    const nextImages = formImages === null ? images || "" : formImages || "";

    if (!imageOnly) {
      if (nextNumbers.length !== 6) {
        setError("Dãy số phải có đúng 6 số");
        return;
      }
      if (!Number.isFinite(nextAmount) || nextAmount <= 0) {
        setError("Số lượng phải lớn hơn 0");
        return;
      }
    }

    setIsSubmitting(true);
    setError("");
    try {
      const resp = await updateSalesTicket(
        token,
        id,
        nextNumbers,
        Math.trunc(nextAmount),
        nextImages,
      );
      if (!resp) {
        setError("Có lỗi xảy ra");
        return;
      }

      setLocalTicket((prev) => ({
        ...(prev || {}),
        numbers: nextNumbers,
        amount: Math.trunc(nextAmount),
        images: nextImages,
      }));

      onRefresh && onRefresh();
      closeEditor();
    } catch (err) {
      setError(err?.message || "Lỗi kết nối");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title className="mb-0">
          Chi tiết vé <span className="text-primary">#{id}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-light">
        {mode === "view" ? (
          <Row>
            <Col md={6}>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <div className="mb-3">
                    <span className="fw-bold d-block text-muted small text-uppercase">
                      Mã vé
                    </span>
                    <span className="h6 text-break">{id}</span>
                  </div>
                  <div className="mb-3">
                    <span className="fw-bold d-block text-muted small text-uppercase">
                      Bộ số
                    </span>
                    <Badge
                      bg="light"
                      className="text-dark border p-2 fs-5 font-monospace"
                      style={{ letterSpacing: "2px" }}
                    >
                      {formatNumbers(displayNumbers || numbers)}
                    </Badge>
                  </div>
                  <div className="mb-3">
                    <span className="fw-bold d-block text-muted small text-uppercase">
                      Đài / Kênh
                    </span>
                    <span className="h6">{channelName || "-"}</span>
                  </div>
                  {isPairGroup && (
                    <div className="mb-3">
                      <span className="fw-bold d-block text-muted small text-uppercase">
                        Số vé trong nhóm
                      </span>
                      <span className="h6">{ticketCount || 0}</span>
                    </div>
                  )}
                  <div className="mb-3">
                    <span className="fw-bold d-block text-muted small text-uppercase">
                      Ngày tạo
                    </span>
                    <span className="h6">
                      {createdAt
                        ? moment(createdAt).format("DD/MM/YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <h6 className="fw-bold text-muted mb-3">
                    Thông tin người dùng
                  </h6>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Người nhập:</span>
                    <span className="fw-bold">{createdByName || "-"}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>Người duyệt:</span>
                    <span className="fw-bold">{approvedByName || "-"}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span>Thời gian duyệt:</span>
                    <span className="fw-bold">
                      {approvedAt
                        ? moment(approvedAt).format("DD/MM/YYYY HH:mm:ss")
                        : "-"}
                    </span>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <h6 className="fw-bold text-muted mb-3">
                    Số lượng & Trạng thái
                  </h6>
                  <div className="d-flex justify-content-between mb-2 border-bottom pb-2">
                    <span>SL Nhập:</span>
                    <span className="fw-bold text-primary">
                      {new Intl.NumberFormat("vi-VN").format(inputAmount || 0)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 border-bottom pb-2">
                    <span>SL Bán:</span>
                    <span className="fw-bold text-success">
                      {new Intl.NumberFormat("vi-VN").format(soldAmount || 0)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2 border-bottom pb-2">
                    <span>SL Tồn:</span>
                    <span className="fw-bold text-secondary">
                      {new Intl.NumberFormat("vi-VN").format(amount || 0)}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between mb-2">
                    <span>SL Hoàn:</span>
                    <span className="fw-bold text-danger">
                      {new Intl.NumberFormat("vi-VN").format(
                        returnedAmount || 0,
                      )}
                    </span>
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                    <span>Trạng thái:</span>
                    <Badge bg={saleStatus.variant} className="px-3 py-2">
                      {saleStatus.text}
                    </Badge>
                  </div>
                  {!isExpired && (
                    <div className="d-flex justify-content-between mt-2">
                      <span className="text-muted">Còn vé:</span>
                      <span className="fw-bold">
                        {new Intl.NumberFormat("vi-VN").format(amount || 0)}
                      </span>
                    </div>
                  )}
                </Card.Body>
              </Card>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h6 className="fw-bold text-muted mb-3">Hình ảnh vé</h6>
                  {displayImages ? (
                    <div className="text-center rounded overflow-hidden border bg-dark">
                      <Image
                        src={displayImages}
                        fluid
                        style={{ maxHeight: "200px" }}
                      />
                    </div>
                  ) : (
                    <div className="text-muted small">Chưa có hình</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        ) : (
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <h6 className="fw-bold text-muted mb-3">
                {editorMode === "image" ? "Đổi hình" : "Sửa vé"}
              </h6>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <Form>
                {editorMode !== "image" && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Dãy số (6 số)</Form.Label>
                      <Form.Control
                        type="text"
                        value={formNumbers}
                        onChange={(e) =>
                          setFormNumbers(
                            normalizeTicketNumbers(e.target.value).slice(0, 6),
                          )
                        }
                        placeholder="Nhập 6 số"
                        maxLength={6}
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold">Số lượng</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={formAmount}
                        onChange={(e) => setFormAmount(e.target.value)}
                        placeholder="Nhập số lượng"
                      />
                    </Form.Group>
                  </>
                )}

                <Form.Group className="mb-2">
                  <Form.Label className="fw-bold">Hình ảnh</Form.Label>
                  <div className="mb-2">
                    {displayImages ? (
                      <Image
                        src={displayImages}
                        fluid
                        style={{ maxHeight: "160px" }}
                      />
                    ) : (
                      <div className="text-muted small">Chưa có hình</div>
                    )}
                  </div>
                  <ImageUpload
                    value={formImages}
                    onChange={(payload) => setFormImages(payload)}
                  />
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
      <Modal.Footer>
        {mode === "view" ? (
          <>
            <Button variant="secondary" onClick={handleHide}>
              Đóng
            </Button>
            <Button
              variant="outline-primary"
              disabled={!canUseEditor}
              onClick={() => {
                openEditTicket();
              }}
            >
              Sửa vé
            </Button>
            <Button
              variant="primary"
              disabled={!canUseEditor}
              onClick={() => {
                openEditImage();
              }}
            >
              Đổi hình
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={closeEditor}
              disabled={isSubmitting}
            >
              Hủy
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                onSubmitEdit({ imageOnly: editorMode === "image" })
              }
              disabled={isSubmitting}
            >
              {isSubmitting ? "Đang lưu..." : "Lưu"}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TicketDetailPopup;
